// @flow
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import { S, DARK } from '~/app/ui/tokens';

type Props = {
  classes: Object,
  value: string,
  text: string,
  onChange: Function,
  label: string
}

function Checkbox({ classes: c, value, onChange, text, label }: Props) {
  const onText = text || 'Enabled';
  const offText = text || 'Disabled';
  // A div that acts as a checkbox: role, state and keyboard, so keyboards, screen readers and AI assistants can use it.
  const toggle = () => onChange(!value);
  const behaviour = {
    role: 'checkbox',
    'aria-checked': !!value,
    tabIndex: 0,
    onClick: toggle,
    onKeyDown: (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        toggle();
      }
    }
  };

  // Simple checkbox + label
  if (label) {
    return (
      <div
        className={c.container}
        {...behaviour}
      >
        <div className={classNames(c.checkbox, value && c.checked)} />
        <span>
          {label}
        </span>
      </div>
    );
  }

  // Checkbox with enabled/disabled text
  return (
    <div
      className={c.container}
      {...behaviour}
    >
      <div className={classNames(c.checkbox, value && c.checked)} />
      <span>
        {value && onText}
        {!value && offText}
      </span>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '400',
    color: 'hsl(0, 0%, 40%)',
    paddingLeft: '10px',
    paddingTop: '10px',
    '&:focus-visible': { outline: `2px solid ${S.fg}`, outlineOffset: 2 },
    [DARK]: { color: S.muted }
  },
  checkbox: {
    width: '16px',
    height: '16px',
    outline: '1px solid #ccc',
    marginRight: '5px',
    padding: '3px',
    transition: 'background ease .2s',
    borderRadius: '2px',
    boxShadow: '0 0 3px #aaa',
    [DARK]: { outlineColor: S.fieldLine, boxShadow: `0 0 3px ${S.fieldLine}` }
  },
  checked: {
    background: 'hsl(160, 50%, 50%)',
    border: '3px solid white',
    [DARK]: { borderColor: S.field }
  }
};

export default injectSheet(styles)(Checkbox);
